import {Injectable} from '@angular/core';
import {ApiHelperService} from './api-helper.service';
import {API} from 'aws-amplify';
import {OverlayService} from './overlay.service';
import {MessageDialogComponent} from '../../views/partials/message-dialog/message-dialog.component';
import {AuthService} from './auth.service';
import {MatDialog} from '@angular/material/dialog';
import {CourseGeoJson, TeamCoursesReport, TeamReport, ViolationReportStatus} from '../model/resources/violation-report';
import {
  OfficialTeam,
  Team,
  TeamCategory,
  TeamPaidAccount,
  TeamPaidPlan,
  TeamPostAcl,
  TeamRelatedAccount,
  TeamStatus,
  TeamType
} from '../model/resources/team';
import {ActivityType} from '../model/resources/activity';
import {MoshicomEvent} from '../model/resources/moshicom';
import {RuntesRace} from '../model/resources/runtes';
import {HttpService} from './http.service';
import {LinkageService} from '../model/resources/tatta-user';

@Injectable({
  providedIn: 'root'
})
export class TeamApiService {

  constructor(
    private apiHelperService: ApiHelperService,
    private authService: AuthService,
    private httpService: HttpService,
    private dialog: MatDialog,
    private overlayService: OverlayService,
  ) {
  }

  async getTeam(teamId: number): Promise<Team | OfficialTeam | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {headers};
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `teams/${teamId}`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async patchTeamStatus(teamId: number, status: TeamStatus): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: {status}
      };
      await this.overlayService.call(API.patch(this.apiHelperService.apiName, `teams/${teamId}`, init));
      return true;
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return false;
    }
  }

  /**
   * 解散済みのチームについて全ての関連リソースを物理削除する
   */
  async deleteTeam(teamId: number): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers
      };
      await this.overlayService.call(API.del(this.apiHelperService.apiName, `teams/${teamId}`, init));
      return true;
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return false;
    }
  }

  async getTeamReports(status: ViolationReportStatus): Promise<TeamReport[]> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return [];
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        queryStringParameters: {status}
      };
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, 'teams/reports', init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return [];
    }
  }

  async patchTeamReport(teamId: number, status: ViolationReportStatus): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }
    if (status === ViolationReportStatus.unanswered) {
      MessageDialogComponent.createErrorDialog(this.dialog, 'Illegal request');
      return false;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: {status}
      };
      await this.overlayService.call(API.patch(this.apiHelperService.apiName, `teams/${teamId}/reports`, init));
      return true;
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return false;
    }
  }

  async getTeamCourseReports(status: ViolationReportStatus): Promise<TeamCoursesReport[]> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return [];
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        queryStringParameters: {status}
      };
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, 'teams/reports/courses', init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return [];
    }
  }

  async getTeamCourseGeoJson(courseUrl: string): Promise<CourseGeoJson | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    const gzipGeoJsonBlob: Blob | undefined = await this.httpService.getBlob(courseUrl);
    if (!gzipGeoJsonBlob) {
      return null;
    }

    const decompressedStream = gzipGeoJsonBlob.stream().pipeThrough(
      new DecompressionStream('gzip'),
    );
    return JSON.parse(await new Response(decompressedStream).text()) as CourseGeoJson;
  }

  async patchTeamCourseReport(uuid: string, status: ViolationReportStatus): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }
    if (status === ViolationReportStatus.unanswered) {
      MessageDialogComponent.createErrorDialog(this.dialog, 'Illegal request');
      return false;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: {status}
      };
      await this.overlayService.call(API.patch(this.apiHelperService.apiName, `teams/reports/courses/${uuid}`, init));
      return true;
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return false;
    }
  }

  async getAllOfficialTeams(type: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity,
                            lastEvaluatedId?: number, lastEvaluatedName?: string): Promise<OfficialTeam[] | null> {
    const limit = 200;
    const res: OfficialTeam[] | null = await this.getOfficialTeams(type, limit, lastEvaluatedId, lastEvaluatedName);
    if (res === null) {
      return null;
    }

    if (res.length < limit) {
      return res;
    }

    return res.concat((await this.getAllOfficialTeams(type, res[limit - 1].id, res[limit - 1].name))!);
  }

  async getOfficialTeams(teamType: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity, limit: number,
                         lastEvaluatedId?: number, lastEvaluatedName?: string): Promise<OfficialTeam[] | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const queryStringParameters: any = {
        teamType,
        limit,
      };
      if (lastEvaluatedId && lastEvaluatedName) {
        queryStringParameters.id = lastEvaluatedId;
        queryStringParameters.name = lastEvaluatedName;
      }
      const init = {
        headers,
        queryStringParameters
      };
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `teams/official`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async searchOfficialTeamsById(teamType: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity,
                                teamId: number): Promise<OfficialTeam[] | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const queryStringParameters: any = {
        searchType: SearchType.searchById,
        teamId,
      };
      const init = {
        headers,
        queryStringParameters
      };
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `teams/official/type/${teamType}`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async searchOfficialTeamsByWord(teamType: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity,
                                  searchType: SearchType.searchByName | SearchType.searchByAccount | SearchType.searchByEmail,
                                  searchWord: string): Promise<OfficialTeam[] | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const queryStringParameters: any = {
        searchType,
        searchWord,
      };
      const init = {
        headers,
        queryStringParameters
      };
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `teams/official/type/${teamType}`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async postOfficialTeam(input: PostOfficialTeamInput): Promise<OfficialTeam | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: input
      };
      return await this.overlayService.call(API.post(this.apiHelperService.apiName, `teams/official`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async promoteOfficialTeam(teamId: number, input: PromoteOfficialTeamInput): Promise<OfficialTeam | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: input
      };
      return await this.overlayService.call(API.patch(this.apiHelperService.apiName, `teams/official/${teamId}/promote`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async patchBasicInfo(teamId: number, input: PatchBasicInfoInput): Promise<OfficialTeam | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: input
      };
      return await this.overlayService.call(API.patch(this.apiHelperService.apiName, `teams/official/${teamId}/account`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async postLicense(teamId: number, input: PostLicenseInput): Promise<OfficialTeam | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: input
      };
      return await this.overlayService.call(API.post(this.apiHelperService.apiName, `teams/official/${teamId}/licenses`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async patchLicense(teamId: number, input: PatchLicenseInput): Promise<OfficialTeam | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: input
      };
      return await this.overlayService.call(API.patch(this.apiHelperService.apiName, `teams/official/${teamId}/licenses`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async sendInvitationsToAll(teamId: number): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers
      };
      await this.overlayService.call(API.post(this.apiHelperService.apiName, `teams/official/${teamId}/invitations`, init));
      return true;
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return false;
    }
  }


  async getMoshicomEvent(moshicomId: number): Promise<MoshicomEvent | null> {
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };
    try {
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `moshicom/${moshicomId}`, init));
    } catch (e: any) {
      console.error(e);
      return null;
    }
  }

  async getRuntesRaces(runtesNum: string): Promise<RuntesRace[] | null> {
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };
    try {
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `runtes/${runtesNum}`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async getRuntesRace(runnetRaceId: number): Promise<RuntesRace | null> {
    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers
    };
    try {
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `runtes/raceIds/${runnetRaceId}`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async getRelatedAccounts(teamId: number): Promise<TeamRelatedAccount[] | null> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return null;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
      };
      return await this.overlayService.call(API.get(this.apiHelperService.apiName, `teams/official/${teamId}/related-accounts`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return null;
    }
  }

  async postRelatedAccount(teamId: number, input: PostRelatedAccountInput): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }

    const headers = await this.apiHelperService.getBasicApiHeader();
    const init = {
      headers,
      body: input
    };
    try {
      return await this.overlayService.call(API.post(this.apiHelperService.apiName, `teams/official/${teamId}/related-accounts`, init));
    } catch (e: any) {
      console.error(e);
      return false;
    }
  }

  async deleteRelatedAccount(teamId: number, email: string): Promise<boolean> {
    if (!this.authService.userAuth?.isAllowManageTeam()) {
      MessageDialogComponent.createErrorDialog(this.dialog, '権限がありません。');
      return false;
    }

    try {
      const headers = await this.apiHelperService.getBasicApiHeader();
      const init = {
        headers,
        body: {email}
      };
      return await this.overlayService.call(API.del(this.apiHelperService.apiName, `teams/official/${teamId}/related-accounts`, init));
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, e.response.data.error);
      return false;
    }
  }

}

export enum SearchType {
  searchById,
  searchByName,
  searchByAccount,
  searchByEmail,
}

export interface PostOfficialTeamInput {
  name: string;
  activityType: ActivityType;
  category: TeamCategory;
  aclPostFeed: TeamPostAcl;
  aclPostTeamCourse: TeamPostAcl;
  aclPostTeamHistory: TeamPostAcl;
  aclCreateIntraEvent: TeamPostAcl;
  aclInviteMember: TeamPostAcl;
  area?: string;
  description: string;
  webLink?: string;
  twitterLink?: string;
  facebookLink?: string;
  instagramLink?: string;
  youtubeLink?: string;
  paidAccount: TeamPaidAccount;
  paidPlan: TeamPaidPlan;
  teamType: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity;
  requiredServices: LinkageService[]; // Patch/Promoteでは所属ユーザー制限に不整合が発生するため、新規作成時のみ利用可能
  eventRequiredServices: LinkageService[];
  // 以下はエントリー自動連携機能を利用する場合に使用
  connectedRunnetRaceIds?: number[];
  connectedRuntesNums?: string[];
  connectedMoshicomIds?: number[];
}

export interface PatchBasicInfoInput {
  paidAccount: TeamPaidAccount;
  teamType: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity;
  eventRequiredServices: LinkageService[];
  // 指定した配列でリプレイスされる。変更しない場合は旧配列をそのまま送信すること
  connectedRunnetRaceIds: number[];
  connectedRuntesNums: string[];
  connectedMoshicomIds: number[];
}

export interface PostLicenseInput {
  // additionalCapacityについて、POST時に50だった場合はcapacity150。この場合50より小さい値は指定できない。新たに100と指定した場合、capacityは200となる
  paidPlan: TeamPaidPlan;
}

export interface PatchLicenseInput {
  // additionalCapacityについて、POST時に50だった場合はcapacity150。この場合50より小さい値は指定できない。新たに100と指定した場合、capacityは200となる
  paidPlan: TeamPaidPlan;
}

export interface PromoteOfficialTeamInput {
  paidAccount: TeamPaidAccount;
  paidPlan: TeamPaidPlan;
  teamType: TeamType.official | TeamType.officialPrivate | TeamType.officialRaceCommunity;
  // 以下はエントリー自動連携機能を利用する場合に使用
  connectedRunnetRaceIds?: number[];
  connectedRuntesNums?: string[];
  connectedMoshicomIds?: number[];
}

export interface PostRelatedAccountInput {
  account: TeamRelatedAccount;
}
